/**
 * Django site origin helpers for Classic Site hybrid auth.
 * Derived from REACT_APP_HOODIE_API_BASE_URL (must include https://).
 */

import { isHoodieApiConfigured } from "../../../api/config";

/**
 * @returns {string|null} e.g. https://do-app.internal.themamahood.co
 */
export function getDjangoSiteOrigin() {
  const raw = (process.env.REACT_APP_HOODIE_API_BASE_URL || "").trim();
  if (!raw) return null;

  try {
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(withProtocol.replace(/\/$/, "")).origin;
  } catch {
    return null;
  }
}

/** @returns {boolean} */
export function isCrossOriginDjango() {
  if (typeof window === "undefined") return false;
  const djangoOrigin = getDjangoSiteOrigin();
  return Boolean(djangoOrigin && djangoOrigin !== window.location.origin);
}

/**
 * Absolute URL to return to this React app after Django auth.
 * @param {string} [returnPath] path+search on current origin, or full URL
 */
export function getAbsoluteReturnUrl(returnPath) {
  if (returnPath && /^https?:\/\//i.test(returnPath)) {
    return returnPath;
  }
  const path =
    returnPath ||
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/classic-site/member");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://localhost";
  return `${origin}${normalized}`;
}

/**
 * Build django-allauth URL on the correct host (React or Django).
 * @param {string} authPath e.g. /accounts/login/
 * @param {string} [returnPath]
 */
export function buildDjangoAuthUrl(authPath, returnPath) {
  const next = getAbsoluteReturnUrl(returnPath);
  const path = authPath.startsWith("/") ? authPath : `/${authPath}`;
  const djangoOrigin = getDjangoSiteOrigin();

  if (djangoOrigin && isCrossOriginDjango()) {
    return `${djangoOrigin}${path}?next=${encodeURIComponent(next)}`;
  }

  return `${path}?next=${encodeURIComponent(
    returnPath ||
      (typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : next)
  )}`;
}

export function isDjangoAuthConfigured() {
  return isHoodieApiConfigured() && Boolean(getDjangoSiteOrigin());
}
